import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { defaultGenericMeta } from "store/common";
import {
  PayByTextApiResponse,
  PayByTextData,
  PayByTextState,
} from "./paybytext-state";
import { fetchPayByText, savePayByText } from "./paybytext-action";

const initialState: PayByTextState = {
  meta: { ...defaultGenericMeta },
  byAccount: {},
};

export const payByTextSlice = createSlice({
  name: "PayByText",
  initialState,
  reducers: {
    updatePayByText(
      state,
      action: PayloadAction<{
        accountInformationId: number;
        data: Partial<PayByTextData>;
      }>
    ) {
      const { accountInformationId, data } = action.payload;
      state.byAccount[accountInformationId] = {
        ...state.byAccount[accountInformationId],
        ...data,
      };
    },
    deletePayByText(state, action: PayloadAction<number>) {
      const accountInformationId = action.payload;
      if (state.byAccount[accountInformationId]) {
        delete state.byAccount[accountInformationId];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayByText.pending, (state) => {
        state.meta = {
          ...state.meta,
          isLoading: true,
          status: "inProcess",
          error: null,
          isSuccess: false,
        };
      })
      .addCase(
        fetchPayByText.fulfilled,
        (state, action: PayloadAction<PayByTextApiResponse[]>) => {
          // Normalize the array into byAccount
          action.payload.forEach((item) => {
            const feature = item.enrollmentFeature;
            const conversation = feature.conversationEnrollment;
            const accountInformationId = feature.accountInformationId;

            state.byAccount[accountInformationId] = {
              id: feature.id,
              accountInformationId,
              phoneNumber: conversation?.phoneNumber ?? "",
              status: item.enrollmentFeatureStatus,
              paymentMethodId: feature.paymentMethodId,
              paymentAmountType: 0,
              paymentAmount: feature.paymentAmount,
              conversationEnrollmentId: feature.conversationEnrollmentId,
            };
          });

          state.meta = {
            ...state.meta,
            isLoading: false,
            isSuccess: true,
            status: "success",
            error: null,
          };
        }
      )
      .addCase(fetchPayByText.rejected, (state) => {
        state.meta = {
          ...state.meta,
          isLoading: false,
          isSuccess: false,
          status: "error",
          error: null,
        };
      })
      .addCase(savePayByText.pending, (state) => {
        state.meta = {
          ...state.meta,
          isLoading: true,
          isSuccess: false,
          status: "inProcess",
          error: null,
        };
      })
      .addCase(
        savePayByText.fulfilled,
        (state, action: PayloadAction<PayByTextData | null>) => {
          const data = action.payload;
          if (data === null) {
            return;
          }

          const existingData = state.byAccount[data.accountInformationId] || {};
          state.byAccount[data.accountInformationId] = {
            ...existingData,
            ...data,
          };
          state.meta = {
            ...state.meta,
            isLoading: false,
            isSuccess: true,
            status: "success",
            error: null,
          };
        }
      )
      .addCase(savePayByText.rejected, (state, action) => {
        state.meta = {
          ...state.meta,
          isLoading: false,
          isSuccess: false,
          status: "error",
          error: action.error.message || "Failed to save Pay By Text data",
        };
      });
  },
});

export const { updatePayByText, deletePayByText } = payByTextSlice.actions;

export default payByTextSlice.reducer;
